import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const TripSplitCalculator = () => {
  // State for input fields
  const [totalTripCost, setTotalTripCost] = useState("");
  const [extraCosts, setExtraCosts] = useState("");
  const [numPeople, setNumPeople] = useState(1);
  const [accommodationCosts, setAccommodationCosts] = useState("");
  const [transportationCosts, setTransportationCosts] = useState("");
  const [mealCosts, setMealCosts] = useState("");
  const [discounts, setDiscounts] = useState("");
  const [totalCostPerPerson, setTotalCostPerPerson] = useState(0);

  // Trigger calculation on any change in inputs
  useEffect(() => {
    calculateTripCost();

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [
    totalTripCost,
    extraCosts,
    numPeople,
    accommodationCosts,
    transportationCosts,
    mealCosts,
    discounts,
  ]);

  // Function to calculate total cost per person
  const calculateTripCost = () => {
    const totalTrip = parseFloat(totalTripCost) || 0;
    const extra = parseFloat(extraCosts) || 0;
    const people = parseInt(numPeople) || 1;
    const accommodation = parseFloat(accommodationCosts) || 0;
    const transportation = parseFloat(transportationCosts) || 0;
    const meals = parseFloat(mealCosts) || 0;
    const discount = parseFloat(discounts) || 0;

    // Total cost calculation
    let totalCost = totalTrip + extra + accommodation + transportation + meals;

    // Apply discount if any (calculate discount amount and subtract it)
    const discountAmount = (totalCost * discount) / 100;
    totalCost -= discountAmount;

    // Calculate cost per person
    const costPerPerson = totalCost / people;

    setTotalCostPerPerson(costPerPerson.toFixed(2));
  };

  return (
    <Layout title="Trip Split Calculator" path="trip-split">
      <section
        className="topics-detail-section section-padding"
        id="topics-detail"
      >
        <div className="container">
          <div className="row">
            {/* Calculator Section (75% space) */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Trip Split Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Total Trip Cost */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="totalTripCost" className="form-label">
                        Total Trip Cost (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Trip Cost:</strong> The total amount for the trip, including all expenses before dividing among participants."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="totalTripCost"
                        step="0.01"
                        placeholder="Enter total trip cost"
                        value={totalTripCost}
                        onChange={(e) => setTotalTripCost(e.target.value)}
                      />
                    </div>

                    {/* Extra Costs */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="extraCosts" className="form-label">
                        Extra Costs (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Extra Costs:</strong> Any additional costs incurred during the trip, like souvenirs, tips, or unexpected expenses."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="extraCosts"
                        step="0.01"
                        placeholder="Enter extra costs"
                        value={extraCosts}
                        onChange={(e) => setExtraCosts(e.target.value)}
                      />
                    </div>

                    {/* Number of People */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="numPeople" className="form-label">
                        Number of People
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Number of People:</strong> Enter the number of people sharing the total cost of the trip."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="numPeople"
                        min="1"
                        placeholder="Enter number of people"
                        value={numPeople}
                        onChange={(e) => setNumPeople(e.target.value)}
                      />
                    </div>

                    {/* Accommodation Costs */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="accommodationCosts"
                        className="form-label"
                      >
                        Accommodation Costs (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Accommodation Costs:</strong> The total cost of accommodation for the trip, like hotel or rental fees."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="accommodationCosts"
                        step="0.01"
                        placeholder="Enter accommodation costs"
                        value={accommodationCosts}
                        onChange={(e) => setAccommodationCosts(e.target.value)}
                      />
                    </div>

                    {/* Transportation Costs */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="transportationCosts"
                        className="form-label"
                      >
                        Transportation Costs (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Transportation Costs:</strong> The total cost for transportation like flights, buses, taxis, or rental cars."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="transportationCosts"
                        step="0.01"
                        placeholder="Enter transportation costs"
                        value={transportationCosts}
                        onChange={(e) => setTransportationCosts(e.target.value)}
                      />
                    </div>

                    {/* Meal Costs */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="mealCosts" className="form-label">
                        Meal Costs (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Meal Costs:</strong> The total amount spent on food and dining during the trip."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="mealCosts"
                        step="0.01"
                        placeholder="Enter meal costs"
                        value={mealCosts}
                        onChange={(e) => setMealCosts(e.target.value)}
                      />
                    </div>

                    {/* Discounts (Optional) */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="discounts" className="form-label">
                        Discounts (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Discounts:</strong> If there are any discounts available for the trip, like group discounts or promotions, enter the discount percentage here."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                        <span
                          className="ms-1 text-muted"
                          style={{ fontSize: "0.9em" }}
                        >
                          (optional)
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="discounts"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="Enter discount percentage"
                        value={discounts}
                        onChange={(e) => setDiscounts(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Total Cost per Person:{" "}
                        <span id="totalCostPerPerson" className="fw-bold ms-3">
                          ₹{totalCostPerPerson}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section (25% space) */}
            <Advertise />
          </div>
        </div>
      </section>
      <section class="introduction-section" id="introduction">
        <div class="container">
          <div class="row">
            <div class="col-lg-12">
              <div class="p-5">
                <h3 class="text-center mb-5">
                  Introduction to the Trip Split Calculator
                </h3>

                <h4 class="mt-3 mb-3">What is a Trip Split Calculator?</h4>
                <p>
                  A **Trip Split Calculator** is a tool designed to help groups
                  of people easily divide the total costs of a trip. It
                  calculates each person’s share of expenses, including
                  accommodation, transportation, meals, and other trip-related
                  costs. The calculator allows for transparency in shared costs,
                  ensuring everyone knows how much they owe, and helps in
                  managing expenses for group travel.
                </p>

                <h4 class="mt-4 mb-3">How to Calculate Trip Split?</h4>
                {/* <p>Calculating the trip cost per person involves using the following formula:</p>
                    <blockquote>
                        **Total Cost per Person** = (Total Trip Cost + Extra Costs + Accommodation Costs + Transportation Costs + Meal Costs) - Discounts ÷ Number of People
                    </blockquote> */}
                <p>To use the calculator:</p>
                <ul>
                  <li>
                    Enter the total trip cost, which includes the overall cost
                    of the trip.
                  </li>
                  <li>
                    Include any additional costs such as activities,
                    entertainment, or extra charges.
                  </li>
                  <li>
                    Provide the accommodation, transportation, and meal costs
                    incurred during the trip.
                  </li>
                  <li>
                    Optionally, apply any discounts or offers that reduce the
                    total cost.
                  </li>
                  <li>Specify the number of people in the group.</li>
                </ul>
                <p>
                  The calculator then uses these inputs to compute the
                  individual share of the total cost instantly.
                </p>

                <h4 class="mt-4 mb-3">
                  What are the Factors on which Trip Split Calculation Depends?
                </h4>
                <p>
                  The trip split calculation depends on several key factors,
                  including:
                </p>
                <ul>
                  <li>
                    <strong>Total Trip Cost:</strong> The combined cost for the
                    entire trip, including all planned activities and expenses.
                  </li>
                  <li>
                    <strong>Extra Costs:</strong> Any unforeseen or additional
                    costs that come up during the trip, such as tips,
                    activities, or emergencies.
                  </li>
                  <li>
                    <strong>Accommodation Costs:</strong> The cost of staying at
                    hotels, resorts, or any other type of lodging.
                  </li>
                  <li>
                    <strong>Transportation Costs:</strong> The cost of getting
                    from one location to another, including flights, taxis, or
                    car rentals.
                  </li>
                  <li>
                    <strong>Meal Costs:</strong> The amount spent on food and
                    beverages during the trip.
                  </li>
                  <li>
                    <strong>Discounts:</strong> Any discounts or promotional
                    offers that reduce the total cost of the trip.
                  </li>
                  <li>
                    <strong>Number of People:</strong> The number of people
                    sharing the trip costs, which determines the per-person
                    split.
                  </li>
                </ul>

                <h4 class="mt-4 mb-3">
                  What are the Benefits of a Trip Split Calculator?
                </h4>
                <p>
                  Using a trip split calculator provides several benefits for
                  group travel:
                </p>
                <ul>
                  <li>
                    <strong>Accurate Cost Distribution:</strong> Ensures
                    everyone contributes fairly and equally based on the total
                    expenses.
                  </li>
                  <li>
                    <strong>Transparency:</strong> Promotes transparency in
                    cost-sharing, preventing confusion or misunderstandings
                    among participants.
                  </li>
                  <li>
                    <strong>Easy Expense Management:</strong> Simplifies the
                    process of tracking and managing shared trip expenses,
                    saving time and effort.
                  </li>
                  <li>
                    <strong>Helps with Budgeting:</strong> Allows travelers to
                    plan better by understanding their share of costs in
                    advance, helping to avoid overspending.
                  </li>
                </ul>

                <h4 class="mt-4 mb-3">
                  Why is it important to use a Trip Split Calculator?
                </h4>
                <p>
                  Using a Trip Split Calculator is important because it
                  simplifies the process of dividing costs among multiple
                  people, preventing misunderstandings and disputes. It ensures
                  that each person pays their fair share based on the actual
                  costs incurred during the trip. Additionally, it promotes
                  transparency, making it clear how much each individual owes,
                  which helps to avoid conflicts and makes managing finances
                  easier. By using this tool, group travel becomes much more
                  organized and stress-free, allowing everyone to enjoy the trip
                  without worrying about the financial details.
                </p>

                <p>
                  In summary, the Trip Split Calculator is a helpful tool for
                  individuals, travel agencies, or group organizers. It
                  streamlines the process of managing group trip costs and
                  ensures that everyone pays their fair share, making it easier
                  to enjoy the trip without worrying about finances.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TripSplitCalculator;
